import type { CalibrationBucket } from "../pages/backtestTypes";
import { formatPercent } from "../lib/format";

/**
 * Inline-SVG calibration chart: predicted-probability bucket on x, realised
 * hit rate on y, diagonal reference line. Bubble size = bucket sample count.
 *
 * No chart library — keeps the bundle lean and the rendering deterministic.
 */
export function CalibrationChart({ buckets }: { buckets: CalibrationBucket[] }) {
  if (buckets.length === 0) {
    return <p className="text-xs text-ink-500">No calibration data.</p>;
  }

  const W = 360;
  const H = 220;
  const PAD = 28;

  const points = buckets.map((b) => ({
    x: PAD + b.predictedAverage * (W - PAD - 8),
    y: H - PAD - b.realisedHitRate * (H - PAD - 8),
    r: 3 + Math.sqrt(b.count) / 4,
    label: `${formatPercent(b.predictedAverage)} predicted → ${formatPercent(b.realisedHitRate)} actual (n=${b.count})`,
  }));

  const ticks = [0.25, 0.5, 0.75];

  return (
    <svg
      role="img"
      aria-label="Calibration chart: predicted probability vs realised hit rate"
      viewBox={`0 0 ${W} ${H}`}
      className="w-full max-w-[420px]"
    >
      {/* Diagonal reference line */}
      <line x1={PAD} y1={H - PAD} x2={W - 8} y2={PAD} className="stroke-ink-100" strokeDasharray="3 3" />
      {/* Axes */}
      <line x1={PAD} y1={H - PAD} x2={W - 8} y2={H - PAD} className="stroke-ink-100" />
      <line x1={PAD} y1={PAD} x2={PAD} y2={H - PAD} className="stroke-ink-100" />
      {ticks.map((t) => (
        <g key={t}>
          <text
            x={PAD + t * (W - PAD - 8)}
            y={H - PAD + 14}
            textAnchor="middle"
            className="fill-ink-500 text-[10px]"
          >
            {formatPercent(t)}
          </text>
          <text
            x={PAD - 6}
            y={H - PAD - t * (H - PAD - 8) + 4}
            textAnchor="end"
            className="fill-ink-500 text-[10px]"
          >
            {formatPercent(t)}
          </text>
        </g>
      ))}
      {/* Bucket points */}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={p.r} className="fill-pitch-700" fillOpacity={0.75}>
          <title>{p.label}</title>
        </circle>
      ))}
      {/* Axis labels */}
      <text x={W / 2} y={H - 4} textAnchor="middle" className="fill-ink-500 text-[10px]">
        Predicted probability
      </text>
      <text
        x={10}
        y={H / 2}
        textAnchor="middle"
        transform={`rotate(-90 10 ${H / 2})`}
        className="fill-ink-500 text-[10px]"
      >
        Realised hit rate
      </text>
    </svg>
  );
}
